'use client';
import { Button, Input, Label, Modal, Switch, useToast } from '@ethsign/ui';
import { shortenWalletAddress } from '@ethsign/utils-web';
import { useMutation, useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { ReactNode, useEffect, useRef, useState } from 'react';

import {
  AuthCodeInput,
  Avatar,
  Card,
  ColorAlertIcon,
  ColorLockIcon,
  ColorMailIcon,
  EmailIcon,
  PasswordInput,
  TelegramIcon,
  WalletIconMini
} from '@/components';
import { PasswordManagerModal, usePasswordManagerModal } from '@/components/Modal/PasswordManagerModal';
import { ENVS, EVENT_MESSAGE_NAME_ENUM, TELEGRAM_LOGIN_URL } from '@/constants';
import { useUserInfo } from '@/hooks';
import {
  bindEmail,
  bindTelegram,
  configNotification,
  fetchStatistics,
  sendEmail,
  switchPasswordManager,
  updateUserInfo
} from '@/services';
import { ICurrentUser, INotification } from '@/types';
import { eventBus, isSupportPasswordManager, isValidEmail, reportGAEvent, WalletType } from '@/utils';

import { vm } from './PageCtrl';
import { EKycSetPrimaryModal } from './components/EKycSetPrimaryModal';
import { SingpassConnect } from './components/SingpassConnect';

//import { UAEConnect } from './components/UAEConnect';
import { SignatureStyles } from './signaturestyles';

const NotificationSectionItem = ({
  title,
  description,
  icon,
  checked,
  disabled,
  type,
  onSuccess
}: {
  title: string;
  description: ReactNode;
  icon: ReactNode;
  checked: boolean;
  disabled: boolean;
  type: INotification['notifyType'];
  onSuccess: () => void;
}) => {
  const { toast } = useToast();
  const { mutateAsync, isPending } = useMutation({
    mutationKey: ['configNotification'],
    mutationFn: (data: INotification) => {
      return configNotification(data);
    }
  });

  const toggleNotify = async (enable: boolean) => {
    await mutateAsync({
      notifyType: type,
      enable
    });
    toast({
      title: 'Success',
      description: 'Notification preferences updated',
      variant: 'success'
    });
    onSuccess();
  };
  return (
    <div className="flex items-center justify-between">
      <div className="flex gap-[12px]">
        {icon}
        <div>
          <div>{title}</div>
          <div className="text-xs text-gray-400">{description}</div>
        </div>
      </div>
      <Switch loading={isPending} checked={checked} disabled={disabled} onCheckedChange={(v) => toggleNotify(v)} />
    </div>
  );
};

interface IUserSectionProps {
  data?: ICurrentUser;
  refetch: () => void;
}

export const NotificationSection = ({ data, refetch }: IUserSectionProps) => {
  return (
    <Card title="Notification" subTitle="Set up notifications">
      <div className="py-[20px] px-[24px] flex flex-col gap-[20px]">
        <NotificationSectionItem
          onSuccess={refetch}
          type="email"
          title="Email Address"
          description={
            data?.email ? (
              <div>
                You will receive notification at <span className="text-gray-700">{data?.email}</span>
              </div>
            ) : (
              'Setup your email to receive notification'
            )
          }
          icon={<EmailIcon />}
          checked={!!data?.emailNotification}
          disabled={!data?.email}
        />
        <NotificationSectionItem
          onSuccess={refetch}
          title={'Telegram'}
          description={
            data?.telegram ? (
              <div>
                You will receive notification at
                <span className="text-gray-700">@{data.telegramNickName || data.telegram}</span>
              </div>
            ) : (
              'Setup your Twitter handle to receive notification'
            )
          }
          icon={<TelegramIcon />}
          checked={!!data?.telegramNotification}
          disabled={!data?.telegram}
          type={'telegram'}
        />
      </div>
    </Card>
  );
};

export const OneTapSection = () => {
  return (
    <Card
      title="1-Tap Encryption"
      subTitle="Encrypt and decrypt contracts with your private key. You don't have to remember any password with 1-Tap Encryption enabled. "
      extra={<Button>Enable</Button>}
    ></Card>
  );
};

const EmailCodeModal = ({ data, refetch: onSuccess }: IUserSectionProps) => {
  const vmData = vm.$useSnapshot();
  const { mutateAsync: requestEmail, isPending: sendLoading } = useMutation({
    mutationKey: ['sendEmail'],
    mutationFn: sendEmail
  });
  const { mutateAsync: bindEmailRequest, isPending: bindLoading } = useMutation({
    mutationKey: ['bindEmail'],
    mutationFn: bindEmail
  });
  const { toast } = useToast();
  const onConfirm = async () => {
    const isValid = isValidEmail(vmData.email);
    if (!isValid) {
      toast({
        title: 'Error',
        description: 'Invalid email',
        variant: 'error'
      });
      return;
    }
    if (vmData.count > 0) {
      toast({
        title: 'Error',
        description: `Please retry after ${vmData.count} seconds`,
        variant: 'error'
      });
      return;
    }
    try {
      await requestEmail({ email: vmData.email });
      toast({
        title: 'Success',
        description: 'Email sent',
        variant: 'success'
      });
      vm.startCountDown();
      vm.setCodeModalVisible(true);
    } catch (e) {
      console.log(e);
    }
  };

  const onSubmitEmail = async () => {
    const code = vm.$getState().code;
    if (!code) {
      toast({
        title: 'Error',
        description: 'Please enter the verification code',
        variant: 'error'
      });
      return;
    }
    await bindEmailRequest({ code: code });
    toast({
      title: 'Success',
      description: 'Email verified',
      variant: 'success'
    });
    onSuccess();
    vm.setCodeModalVisible(false);
    vm.setEmailVisible(false);
  };

  return (
    <>
      <div className="flex items-center justify-between mt-[30px]">
        <div className="flex gap-[12px] flex-1">
          <EmailIcon />
          <div className="flex-1">
            <div className="text-sm text-gray-500">Email Address</div>
            {vmData.emailVisible ? (
              <div className="mt-[6px]">
                <div className="flex items-center w-full">
                  <div className="flex-1">
                    <Input
                      placeholder="Enter your email"
                      onChange={(e) => vm.setEmail(e.target.value)}
                      value={vmData.email}
                    />
                  </div>
                  <Button
                    loading={sendLoading}
                    onClick={onConfirm}
                    disabled={!vmData.email}
                    className={'ml-[16px]'}
                    size={'sm'}
                  >
                    Confirm
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => vm.setEmailVisible(false)}
                    className={'ml-[8px]'}
                    size={'sm'}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : data?.email ? (
              <div className="flex gap-[8px]">
                <div className="text-sm text-gray-900">{data?.email}</div>
                <div className="text-sm text-primary-900 cursor-pointer" onClick={() => vm.setEmailVisible(true)}>
                  Edit
                </div>
              </div>
            ) : (
              <div className="text-sm text-primary-900 cursor-pointer" onClick={() => vm.setEmailVisible(true)}>
                Enter Email Address
              </div>
            )}
          </div>
        </div>
      </div>
      <Modal
        className={'sm:w-[472px]'}
        open={vmData.codeModalVisible}
        onOpenChange={(v: boolean) => vm.setCodeModalVisible(v)}
        onCancel={() => vm.setCodeModalVisible(false)}
        onConfirm={onSubmitEmail}
        confirmText={'Verify'}
        confirmButtonProps={{
          loading: bindLoading,
          variant: 'primary'
        }}
      >
        <div className="flex items-center flex-col justify-center">
          <ColorMailIcon />
          <div className="text-lg font-medium text-gray-900 mt-[20px]">Verify Your Email</div>
          <div className="text-sm text-gray-500 mt-[8px] text-center">
            We&apos;ve sent a verification code to <span className="font-semibold">{vmData.email}</span>.
          </div>
        </div>
        <div className="mt-[20px]">
          <AuthCodeInput onChange={vm.setCode} placeholder={'0'} />
          <div className="text-sm text-gray-500 mt-[6px]">
            Didn&apos;t get a code?
            <span className="text-sm underline cursor-pointer ml-[6px]" onClick={onConfirm}>
              Click to resend.
            </span>
          </div>
        </div>
      </Modal>
    </>
  );
};

const ProfileSection = ({ data, refetch: fetchUser }: IUserSectionProps) => {
  const vmData = vm.$useSnapshot();
  const { toast } = useToast();
  const ref = useRef<Window | null>(null);
  const { data: statistics } = useQuery({
    queryKey: ['user-statistics'],
    queryFn: () => fetchStatistics()
  });

  const { mutateAsync: updateUser, isPending: updateUserLoading } = useMutation({
    mutationFn: updateUserInfo,
    mutationKey: ['updateUserInfo']
  });

  useEffect(() => {
    const handleTelegramConnect = async (event: any) => {
      const { data, origin } = event;
      if (origin === TELEGRAM_LOGIN_URL) {
        ref.current?.close();
        if (!data.payload) {
          toast({
            title: 'Error',
            description: "Couldn't connect to Telegram",
            variant: 'error'
          });
        } else {
          await bindTelegram({
            callbackData: data.payload
          });
          toast({
            title: 'Success',
            description: 'Telegram connected',
            variant: 'success'
          });
          fetchUser();
        }
      }
    };
    eventBus.on(EVENT_MESSAGE_NAME_ENUM.TELEGRAM_LOGIN_SUCCESS, handleTelegramConnect);
    return () => {
      eventBus.off(EVENT_MESSAGE_NAME_ENUM.TELEGRAM_LOGIN_SUCCESS, handleTelegramConnect);
    };
  }, []);

  const loginTelegram = () => {
    const tgWindow = window.open(
      `${TELEGRAM_LOGIN_URL}?bot_name=${ENVS.VITE_TELEGRAM_BOT_NAME}`,
      'telegram',
      'width=500,height=500'
    );
    ref.current = tgWindow;
  };

  const handleConfirmNickName = async () => {
    await updateUser({ nickName: vmData.nickname });
    toast({
      title: 'Success',
      description: 'Name updated',
      variant: 'success'
    });
    vm.setNameEdit(false);
    fetchUser();
  };

  useEffect(() => {
    if (data?.nickName) {
      vm.setNickname(data?.nickName);
    }
  }, [data]);

  return (
    <Card>
      <div className="flex justify-between items-end">
        {data ? (
          <div
            className={classNames(['flex gap-[12px] flex-1'], {
              'items-center': !vmData.isNameEdit
            })}
          >
            <Avatar address={data?.displayAddress} />
            {vmData.isNameEdit ? (
              <div className="flex-1">
                <div className="text-gray-500 mb-[6px] text-[15px]">Name</div>
                <Input
                  placeholder="Enter your name here"
                  value={vmData.nickname}
                  onChange={(e) => vm.setNickname(e.target.value)}
                />
              </div>
            ) : (
              <div className="font-bold text-[25px]">
                {data?.nickName || data?.ens || shortenWalletAddress(data?.displayAddress, 'shorter')}
              </div>
            )}
          </div>
        ) : null}
        {vmData.isNameEdit ? (
          <>
            <Button
              loading={updateUserLoading}
              className={'ml-[12px]'}
              onClick={handleConfirmNickName}
              disabled={!vmData.nickname}
              size="sm"
            >
              Confirm
            </Button>
            <Button size="sm" variant={'outline'} className={'ml-[8px]'} onClick={() => vm.setNameEdit(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <Button variant="outline" onClick={() => vm.setNameEdit(true)}>
            {data?.nickName ? 'Edit Name' : 'Set Name'}
          </Button>
        )}
      </div>
      <div className="flex gap-[56px] mt-[24px]">
        <div>
          <div className="text-sm text-gray-500">Total Contracts Sent</div>
          <div className="font-bold text-sm text-gray-900">{statistics?.sendedByMeCount || '-'}</div>
        </div>
        <div>
          <div className="text-sm text-gray-500">Total Contracts Signed</div>
          <div className="font-bold text-sm text-gray-900">{statistics?.signedByMeCount || '-'}</div>
        </div>
      </div>
      <div className="flex gap-[12px] items-center mt-[30px]">
        <WalletIconMini />
        <div>
          <div className="text-sm text-gray-500">Wallet Address</div>
          <div className="font-medium text-sm text-gray-900">{data?.displayAddress}</div>
        </div>
      </div>
      <EmailCodeModal data={data} refetch={fetchUser} />
      <div className="flex items-center justify-between mt-[30px]">
        <div className="flex gap-[12px]">
          <TelegramIcon />
          <div>
            <div className="text-sm text-gray-500">Telegram</div>
            {data?.telegram ? (
              <div className="flex gap-[8px]">
                <div className="text-sm text-gray-900">@{data?.telegramNickName || data?.telegram}</div>
                <div className="text-sm text-primary-900 cursor-pointer" onClick={loginTelegram}>
                  Edit
                </div>
              </div>
            ) : (
              <div className="text-sm text-primary-900 cursor-pointer" onClick={loginTelegram}>
                Connect to Telegram
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

const PasswordManager = () => {
  const vmData = vm.$useSnapshot();
  const { toast } = useToast();
  const { user, fetchUser } = useUserInfo();
  const isSupport = isSupportPasswordManager(user?.chainType as WalletType);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const { mutateAsync: switchRequest, isPending: loading } = useMutation({
    mutationKey: ['switchPasswordManager'],
    mutationFn: (enable: boolean) => switchPasswordManager({ enable })
  });
  const handleEnablePassword = async () => {
    await switchRequest(true);
    fetchUser();
    toast({
      title: 'Success',
      description: 'Your have successfully enabled EthSign Password Manager. ',
      variant: 'success'
    });
    reportGAEvent('pc_account_togglePasswordManager', {
      address: user!.address,
      enabled: true
    });
  };
  const handleDisablePassword = async () => {
    await switchRequest(false);
    fetchUser();
    toast({
      title: 'Success',
      description: 'Your have successfully disabled EthSign Password Manager. ',
      variant: 'success'
    });
    reportGAEvent('pc_account_togglePasswordManager', {
      address: user!.address,
      enabled: false
    });
    vm.toggleDisablePasswordModal(false);
  };
  const { open, onRetry, setOpen, steps, onEnable } = usePasswordManagerModal({
    onSuccess: async () => {
      handleEnablePassword();
    }
  });
  const togglePasswordManager = () => {
    if (user?.enableIdentity) {
      vm.toggleDisablePasswordModal(true);
    } else {
      if (user?.identityInitialized) {
        handleEnablePassword();
      } else {
        if (isSupport) {
          onEnable();
        } else {
          vm.toggleMasterPasswordModal(true);
        }
      }
    }
  };

  const handleConfirmPassword = async () => {
    setPasswordLoading(true);
    await onEnable(vmData.masterPassword);
    setPasswordLoading(false);
    vm.toggleMasterPasswordModal(false);
  };

  return (
    <>
      <Card className={'overflow-visible'}>
        <div className={'flex justify-between'}>
          <div>
            <div className={'text-gray-900 text-lg font-medium'}>Password Manager</div>
            <div className={'text-gray-500 text-sm'}>
              {isSupport ? (
                <>
                  Say goodbye to password hassles. EthSign Password Manager allows you to decrypt contract without
                  remembering the password.{' '}
                  <a
                    className={'text-primary-900 text-sm'}
                    href={'https://docs.ethsign.xyz/ethsign-next/ethsign-next/technical-overview/data-encryption'}
                    target={'_blank'}
                    rel="noreferrer"
                  >
                    Read more
                  </a>
                </>
              ) : (
                'Encrypt and decrypt contracts with your master password manager. You can decrypt all encrypted contract with your master password manager. '
              )}
            </div>
          </div>
          <div className={'pt-[14px] ml-[50px]'}>
            <Switch checked={user?.enableIdentity} loading={loading} onClick={togglePasswordManager} />
          </div>
        </div>
      </Card>
      <PasswordManagerModal open={open} onOpenChange={setOpen} onConfirm={onRetry} steps={steps} />
      <Modal
        open={vmData.disablePasswordModalVisible}
        onOpenChange={vm.toggleDisablePasswordModal}
        className={'md:w-[475px] max-w-max sm:rounded-xl'}
        confirmText={'Disable'}
        cancelText={'Cancel'}
        onConfirm={handleDisablePassword}
        confirmButtonProps={{
          variant: 'primary',
          loading: loading
        }}
      >
        <div className="flex items-center flex-col justify-center">
          <ColorAlertIcon />
          <div className="text-lg font-medium text-gray-900 mt-[20px] mb-[20px] text-center px-[40px]">
            Are you sure you want to disable Password Manager?
          </div>
          <div className="text-sm text-gray-500 text-center px-[20px] mb-[-12px]">
            EthSign Password Manager helps you manage and decrypt files without having to remember all the passwords for
            the contracts.
          </div>
        </div>
      </Modal>
      <Modal
        open={vmData.masterPasswordModalVisible}
        onOpenChange={vm.toggleMasterPasswordModal}
        className={'md:w-[475px] max-w-max sm:rounded-xl'}
        onConfirm={handleConfirmPassword}
        confirmButtonProps={{
          variant: 'primary',
          loading: loading || passwordLoading
        }}
      >
        <div className="flex items-center flex-col justify-center gap-5">
          <ColorLockIcon />
          <div className="text-lg font-medium text-gray-900 text-center">Set Master Password.</div>
          <div className="text-sm text-gray-500 text-center">
            Set a master password for your account and you can use it to decrypt all your contracts.
          </div>
          <div className={'w-full mb-[-12px]'}>
            <Label className={'text-left w-full'}>Master Password</Label>
            <PasswordInput
              className={'mt-[6px]'}
              value={vmData.masterPassword}
              onChange={(e) => vm.setMasterPassword(e.target.value)}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export const EKYCSection = () => {
  const { user } = useUserInfo();
  return (
    <Card
      className={'overflow-visible'}
      title="eKYC"
      extra={user.uaepassUserId && user.singpassUserId ? <EKycSetPrimaryModal /> : null}
      subTitle="Link your legal digital identity to your wallet address. This connection will appear when applying visual
          signatures and verifying signatures using EthSign."
    >
      <SingpassConnect isPrimary={user.eKyc === 'singpass'} />
      {/*  <UAEConnect isPrimary={user.eKyc === 'uaepass'} /> */}
    </Card>
  );
};

export default function PersonalSettings() {
  const { user: data, fetchUser } = useUserInfo();
  return (
    <div className="p-[20px] flex gap-[20px] bg-white h-[calc(100vh-72px)] overflow-hidden">
      <div className="flex-1 flex flex-col gap-[20px]">
        <ProfileSection data={data} refetch={fetchUser} />
        {/*<NotificationSection data={data} refetch={fetchUser} />*/}
      </div>
      <div className="flex-1 flex flex-col gap-[20px] h-full">
        {/*{data?.chainType === WalletType.EVM ? <OneTapSection /> : null}*/}
        <EKYCSection />
        <PasswordManager />
        <div className={'flex-1 min-h-0'}>
          <SignatureStyles />
        </div>
      </div>
    </div>
  );
}
